import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/shared/api/axios';
import { isValidEmail } from '@/shared/lib/validators';
import { useThemeColors } from '@/shared/theme/useThemeColors';
import { semantic, typography, shadow, radius } from '@/shared/theme/tokens';
import { ROUTES } from '@/shared/config/constants';

export default function LoginPage() {
  const navigate = useNavigate();
  const c = useThemeColors();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);

  const emailValid = isValidEmail(email);
  const showError = touched && email && !emailValid;

  async function handleSubmit(e) {
    e.preventDefault();
    setTouched(true);
    setError('');

    if (!emailValid) return;

    setLoading(true);
    try {
      await api.post('/auth/otp/request', { email });
      navigate(ROUTES.OTP, { state: { email } });
    } catch (err) {
      setError(err.message ?? 'Unable to send OTP. Try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: c.bgPage,
      position: 'relative',
      overflow: 'hidden',
      padding: '24px 16px',
      fontFamily: typography.fontFamily.body,
    }}>
      {/* Background gradient */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1,
        background: 'radial-gradient(ellipse 120% 80% at 50% -10%, rgba(32,36,117,0.06) 0%, transparent 70%)',
      }} />
      <div style={{
        position: 'absolute', top: '-80px', right: '-80px', width: '400px', height: '400px',
        borderRadius: '50%', background: 'radial-gradient(circle, rgba(62,181,231,0.08) 0%, transparent 70%)',
        zIndex: 1, pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-100px', left: '-100px', width: '500px', height: '500px',
        borderRadius: '50%', background: 'radial-gradient(circle, rgba(32,36,117,0.07) 0%, transparent 70%)',
        zIndex: 1, pointerEvents: 'none',
      }} />

      {/* Card */}
      <div style={{
        position: 'relative', zIndex: 10, width: '100%', maxWidth: '420px',
        background: c.bg, borderRadius: radius['3xl'], border: `1px solid ${c.borderSubtle}`,
        boxShadow: shadow.cardFull,
        overflow: 'hidden',
      }}>
        {/* Accent bar */}
        <div style={{ height: '3px', background: 'linear-gradient(90deg, #202475 0%, #1e4396 40%, #3EB5E7 100%)' }} />

        <div style={{ padding: '36px 40px 28px' }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{
              width: '44px', height: '44px', borderRadius: radius.xl,
              background: 'linear-gradient(135deg, #202475 0%, #1e4396 60%, #3EB5E7 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, boxShadow: shadow.btnSm,
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 26, color: '#ffffff', fontVariationSettings: "'FILL' 1, 'wght' 400" }}>
                widgets
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: c.textPrimary, letterSpacing: typography.letterSpacing.normal, lineHeight: 1.2 }}>
                Industrial AutoPilot
              </span>
              <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: c.textMuted, letterSpacing: typography.letterSpacing.wider, textTransform: 'uppercase' }}>
                by Ripik AI
              </span>
            </div>
          </div>

          {/* Divider */}
          <div style={{ height: '1px', background: `linear-gradient(90deg, ${c.borderSubtle} 0%, transparent 100%)`, marginBottom: '24px' }} />

          {/* Headline */}
          <div style={{ marginBottom: '28px' }}>
            <h1 style={{ fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize['5xl'], fontWeight: typography.fontWeight.bold, color: c.textPrimary, letterSpacing: typography.letterSpacing.tight, lineHeight: 1.3, margin: '0 0 8px' }}>
              Sign in to your workspace
            </h1>
            <p style={{ fontSize: typography.fontSize.lg, color: c.textMuted, lineHeight: 1.6, margin: 0, fontWeight: typography.fontWeight.normal }}>
              Enter your work email to receive a one-time access code.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: c.textLabel, letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase' }} htmlFor="email">
                Work Email Address
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span className="material-symbols-outlined" style={{
                  position: 'absolute', left: '12px', fontSize: '18px', pointerEvents: 'none', transition: 'color 0.15s',
                  color: showError ? semantic.error : email && emailValid ? semantic.brandAccent : c.textPlaceholder,
                }}>
                  alternate_email
                </span>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  onBlur={() => setTouched(true)}
                  placeholder="you@enterprise.com"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    background: c.bgSubtle,
                    border: `1.5px solid ${showError ? semantic.error : email && emailValid ? semantic.brandAccent : c.borderInput}`,
                    borderRadius: radius.lg,
                    padding: '12px 40px',
                    minHeight: '44px',
                    fontSize: typography.fontSize.lg,
                    fontFamily: typography.fontFamily.body,
                    color: c.textPrimary,
                    outline: 'none',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                    boxShadow: email && emailValid
                      ? '0 0 0 3px rgba(62,181,231,0.12)'
                      : showError
                      ? '0 0 0 3px rgba(186,26,26,0.10)'
                      : 'none',
                  }}
                />
                {email && emailValid && (
                  <span className="material-symbols-outlined" style={{ position: 'absolute', right: '12px', fontSize: '18px', pointerEvents: 'none', color: semantic.brandAccent }}>
                    check_circle
                  </span>
                )}
              </div>
              {(error || showError) && (
                <p style={{ fontSize: typography.fontSize.base, color: semantic.error, margin: 0, display: 'flex', alignItems: 'center', fontWeight: typography.fontWeight.medium }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 13, marginRight: 4, verticalAlign: 'middle' }}>error</span>
                  {error || 'Please enter a valid work email address.'}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                background: 'linear-gradient(90deg, #202475 0%, #1e4396 50%, #2a7ab5 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: radius.lg,
                padding: '13px 20px',
                fontSize: typography.fontSize.md,
                fontFamily: typography.fontFamily.body,
                fontWeight: typography.fontWeight.semibold,
                letterSpacing: typography.letterSpacing.wider,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'opacity 0.15s, transform 0.1s, box-shadow 0.15s',
                boxShadow: shadow.btnPrimary,
                opacity: loading ? 0.75 : 1,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? (
                <span className="material-symbols-outlined" style={{ fontSize: 18, animation: 'spin 1s linear infinite' }}>refresh</span>
              ) : (
                <>
                  <span>Continue with Email</span>
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
                </>
              )}
            </button>
          </form>

        </div>

        {/* Security badge */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 40px 14px', borderTop: `1px solid ${c.divider}`, background: c.bgSubtle }}>
          <span className="material-symbols-outlined" style={{ fontSize: 12, color: c.textSubtle, marginRight: 4 }}>lock</span>
          <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle, letterSpacing: typography.letterSpacing.wide }}>Secured by Ripik AI</span>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
